// Structure, Content and Members: the last three steps of the build flow.
// Structure and Content are the course wizard's own steps, shown under the
// flow's stepper and named for the format being built. So is Members, except:
//  - a program for roles has its own Members step (./RoleMembersStep.tsx):
//    the program is for its roles only, so there is no audience to edit there
//    and nobody to invite by name; and
//  - a program reopened once published goes straight to Members, without the
//    wizard (its content is out; only Invite & Finish is left).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import CreationMembersStep from '../../Program/components/learning-lifecycle/CreationMembersStep';
import { AIMDEmbeddedWizard } from '../../aimd-v2/AIMDv2Page';
import type { LessonTags } from '../../aimd-v2/lessonTags';
import { AIMD_STEPS, type AIMDStepId } from '../../aimd-v2/types';
import type { SetupRole } from '../capabilitySetup';
import { LINE, Stepper } from '../designSystem';
import RoleMembersStep from './RoleMembersStep';
import { BUILD_STEPS, STRUCTURE_STEP } from './trainingSnapshot';

const MEMBERS_STEP = BUILD_STEPS.length - 1;

const TrainingBuildSteps: React.FC<{
  groupId: string;
  /** The program's name, for the toast once it is published. */
  programName?: string;
  /** "Course", "Short Course" or "Brief". */
  formatTitle: string;
  /** A program for roles (every one built since all of them are): the roles it is for, and their names as kept with it. */
  roleBased?: { roles: SetupRole[]; roleNames: string[] };
  /** The capabilities it builds and its other outcomes: Structure shows each lesson's, and asks for a new one's. */
  lessonTags: LessonTags;
  /** Reopened once published: only Members is left. */
  published?: boolean;
  /** The build moved on a step, and with it the program along its card's pipeline. */
  onMoved?: () => void;
  /** Invite & Finish: the program is built and its people invited. */
  onFinished: () => void;
}> = ({ groupId, programName, formatTitle, roleBased, lessonTags, published = false, onMoved, onFinished }) => {
  // The wizard's steps line up with the flow's from Structure on.
  const [at, setAt] = useState(published ? MEMBERS_STEP : STRUCTURE_STEP);
  const onStepChange = useCallback(
    (stepId: AIMDStepId) => setAt(Math.max(STRUCTURE_STEP, AIMD_STEPS.indexOf(stepId))),
    []
  );
  const moved = useRef(onMoved);
  moved.current = onMoved;
  const opened = useRef(false);
  useEffect(() => {
    // Not on opening: only once a step is left.
    if (opened.current) moved.current?.();
    opened.current = true;
  }, [at]);
  const labels = useMemo(
    () => ({ structureTitle: `${formatTitle} Structure`, generateContent: `Build ${formatTitle} Lessons` }),
    [formatTitle]
  );
  // The same tags object while they read the same, so the wizard below is not redrawn for nothing.
  const tagsKey = JSON.stringify(lessonTags);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tags = useMemo(() => lessonTags, [tagsKey]);
  const stepper = <Stepper steps={BUILD_STEPS} current={at} />;
  if (roleBased && at === MEMBERS_STEP) {
    return (
      <RoleMembersStep
        groupId={groupId}
        programName={programName}
        roles={roleBased.roles}
        roleNames={roleBased.roleNames}
        stepper={stepper}
        onFinished={onFinished}
      />
    );
  }
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, px: '24px', py: '14px', borderBottom: `1px solid ${LINE}` }}>{stepper}</Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {published ? (
          <CreationMembersStep groupId={groupId} onFinish={onFinished} />
        ) : (
          <AIMDEmbeddedWizard
            groupId={groupId}
            onFinish={onFinished}
            onStepChange={onStepChange}
            labels={labels}
            lessonTags={tags}
          />
        )}
      </Box>
    </Box>
  );
};

export default TrainingBuildSteps;
